import { useState, type CSSProperties } from 'react';
import { clsx } from 'clsx';
import { Icon } from './icons';

export type AvatarSize = 'row' | 'tile' | 'chat' | 'greeting' | 'banner' | 'needs-you' | number;
export interface AvatarProps {
  name: string; seed?: string; src?: string | null; initials?: string;
  kind?: 'person' | 'agent' | 'group'; shape?: 'circle' | 'rounded-square';
  size?: AvatarSize; presence?: 'online' | 'offline' | 'away';
  status?: 'idle' | 'working' | 'needs-you'; members?: readonly AvatarProps[];
  symbol?: string; label?: string; className?: string;
}

const palette = [
  ['#8E9BFF', '#5B5FE9'], ['#7CC4FA', '#2F86D9'], ['#5ED6C0', '#129A84'], ['#F9BE5B', '#D4730F'],
  ['#FB8FA6', '#E0456A'], ['#C3B6FD', '#8B7CF6'], ['#6FD69A', '#1E9E55'], ['#A7B1C2', '#6E7A8C'],
] as const;
const orbPalette = [
  ['#C4B5FD', '#8B5CF6', '#EC4899'], ['#A5F3FC', '#22B8CF', '#6366F1'], ['#FDE68A', '#F97316', '#E11D48'],
  ['#BBF7D0', '#10B981', '#0EA5E9'], ['#FBCFE8', '#EC4899', '#8B5CF6'],
] as const;

/** Swift StableHash.fnv1a64, including wrapping arithmetic and UTF-8 seeds. */
function stableHash(seed: string): bigint {
  let hash = 0xcbf29ce484222325n;
  for (const byte of new TextEncoder().encode(seed)) hash = BigInt.asUintN(64, (hash ^ BigInt(byte)) * 0x100000001b3n);
  return hash;
}
export function avatarColor(seed: string): readonly [string, string] {
  return palette[Number(stableHash(seed) % 8n)]!;
}

export function avatarInitials(name: string): string {
  const letters = name.split(/[ _.]+/u).filter(Boolean).slice(0, 2).map(word => Array.from(word)[0]?.toLocaleUpperCase() ?? '').join('');
  return letters || '?';
}

// Native GroupMosaicLayout: center an incomplete first row, with a 6% inset and 4% gutters.
function mosaicTiles(count: number): CSSProperties[] {
  if (count <= 1) return [{ inset: 0 }];
  const columns = count <= 4 ? 2 : 3; const rows = Math.ceil(count / columns);
  const side = (1 - .12 - (columns - 1) * .04) / columns; const height = rows * side + (rows - 1) * .04;
  return Array.from({ length: rows }, (_, row) => {
    const inRow = row === 0 ? count - columns * (rows - 1) : columns; const width = inRow * side + (inRow - 1) * .04;
    return Array.from({ length: inRow }, (_, column) => ({ insetInlineStart: `${((1 - width) / 2 + column * (side + .04)) * 100}%`,
      top: `${((1 - height) / 2 + row * (side + .04)) * 100}%`, width: `${side * 100}%`, height: `${side * 100}%` }));
  }).flat();
}

export function Avatar({ name, seed = name, src, initials, kind = 'person', shape, size = 'row', presence,
  status = 'idle', members, symbol, label, className }: AvatarProps) {
  const [failedSource, setFailedSource] = useState<string>();
  const [loadedSource, setLoadedSource] = useState<string>();
  const [top, bottom] = avatarColor(seed);
  const [orbLight, orbMid, orbEdge] = orbPalette[seed ? Number(stableHash(seed) % 5n) : 0]!;
  const side = typeof size === 'number' ? `${size}px` : `var(--hylo-metric-${size}-avatar)`;
  const style = { '--avatar-size': side, '--avatar-top': top, '--avatar-bottom': bottom,
    '--orb-light': orbLight, '--orb-mid': orbMid, '--orb-edge': orbEdge } as CSSProperties;
  const tiles = members?.slice(0, 9);
  const positions = tiles ? mosaicTiles(tiles.length) : [];
  return <span className={clsx('hylo-avatar', `hylo-avatar--${kind}`, shape && `hylo-avatar--${shape}`, className)}
    style={style} role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true}
    data-presence={presence} data-status={status}>
    <span className="hylo-avatar__face">
      {tiles?.length ? <span className="hylo-avatar__mosaic" data-count={tiles.length}>
          {tiles.map((member, i) => <span className="hylo-avatar__mosaic-tile" key={i} style={positions[i]}><Avatar {...member} members={undefined} size={24} shape="rounded-square" /></span>)}
        </span> : kind === 'agent' ? <span className="hylo-avatar__orb" />
          : symbol ? <Icon name={symbol} /> : <span className="hylo-avatar__initials">{initials ?? avatarInitials(name)}</span>}
      {src && failedSource !== src && <img src={src} alt="" width="100" height="100" draggable={false}
        className={loadedSource === src ? 'is-loaded' : undefined} onLoad={() => setLoadedSource(src)} onError={() => setFailedSource(src)} />}
    </span>
    {kind !== 'agent' && presence === 'online' && status === 'idle' && <span className="hylo-avatar__presence" />}
    {status !== 'idle' && <span className={`hylo-avatar__status hylo-avatar__status--${status}`} />}
  </span>;
}

export interface AvatarStackProps { avatars: readonly AvatarProps[]; size?: AvatarSize; maxCount?: number; label?: string; className?: string }
export function AvatarStack({ avatars, size = 'chat', maxCount = 5, label, className }: AvatarStackProps) {
  return <span className={clsx('hylo-avatar-stack', className)} role={label ? 'img' : undefined} aria-label={label} aria-hidden={!label || undefined}>
    {avatars.slice(0, maxCount).map((avatar, i) => <Avatar key={i} {...avatar} size={size} />)}
  </span>;
}
